/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useUnauthorizedContext } from '../../Shared/hooks/useUnauthorizedContextHook';
import { ErrorTypes } from '../../Shared/resources/Enums';
import doxUploaderCDMService from '../../Shared/services/doxUploaderCDMServices';
import { getRandomHexString } from '../../Shared/services/Util';
import { useDoxUploaderContext } from '../doxUploader';
import useDuplicateDetection from './useDuplicateDetectionHook';
import documentApi from 'services/api/documentApi';
import { isValidSignaturePageFileName, getExpectedSignaturePageFileName } from 'strategies/SignaturePageValidation';

/**
 * Hook to handle the uploading of a file for a given tax year.
 * @param {any} fileUpload
 * @param {any} taxYear
 * @param {boolean} isSignaturePageContext
 * @param {string} filerName
 */
export const useFileUpload = (fileUpload, cancel, layoutType, isSignaturePageContext = false, filerName = null) => {
    const isReadyToUpload = Boolean((fileUpload || {}).isReadyToUpload)

    const errorsWithMessaging = [ErrorTypes.FILE_TOO_BIG, ErrorTypes.INVALID_FILE_TYPE, ErrorTypes.UNKNOWN_ERROR]

    const handleUnauthorized = useUnauthorizedContext();
    const api = documentApi.create(handleUnauthorized);

    const { ticketId, existingFiles, onUploadCompleted, onRetryUpload } = useDoxUploaderContext()
    const { hasCheckedForDupes, duplicateFileId, suffixedFileName } = useDuplicateDetection(fileUpload.file, existingFiles)

    // if this is null, the file will be saved with its original filename.
    const [renameFileAs, setRenameFileAs] = useState(null)
    const [hasResolvedConflicts, setHasResolvedConflicts] = useState(false)

    const [percentUploaded, setPercentUploaded] = useState(0)
    const [isUploadComplete, setIsUploadComplete] = useState(false)
    const [uploadError, setUploadError] = useState(null)

    const [cancelSource, setCancelSource] = useState(null)
    const [cancelUpload, setCancelUpload] = useState(false)

    // handlers
    const handleCancelClick = () => {
        setCancelUpload(true)
    }

    const handleRetryClick = () => {
        setUploadError(null)
        setHasResolvedConflicts(false)

        // handle requeueing
        if (onRetryUpload) {
            resetUpload()
            setTimeout(() => {
                onRetryUpload(fileUpload.qPosition)
                setHasResolvedConflicts(true)
            }, 150)
        }
    }

    const handleKeepBothClick = () => {
        setUploadError(null)
        setRenameFileAs(suffixedFileName)
        setHasResolvedConflicts(true)
    }

    const handleReplaceClick = async () => {
        setUploadError(null)
        setHasResolvedConflicts(false)

        // handle requeueing
        if (onRetryUpload) {
            resetUpload()
            onRetryUpload(fileUpload.qPosition)
        }

        setHasResolvedConflicts(true)
    }

    const checkIfAppleCameraUpload = () => {
        if (fileUpload.file && fileUpload.file.name === 'image.jpg' && (layoutType === 'mobile' || layoutType === 'tablet')) {
            setRenameFileAs(`image(${getRandomHexString(4)}).jpg`)
        }
    }

    const normalizeFileName = (fileName) => {
        if (fileName.toLowerCase().endsWith('.pdf.pdf')) {
            return fileName.slice(0, -4);
        }
        return fileName;
    };

    const checkIfSignaturePageUpload = () => {
        if (isSignaturePageContext && fileUpload.file && filerName) {
            const clientFirstName = filerName.split(' ')[0];
            const rawFileName = fileUpload.file.name;
            const fileName = normalizeFileName(rawFileName);
            
            if (isValidSignaturePageFileName(fileName, clientFirstName)) {
                const expectedFileName = getExpectedSignaturePageFileName(clientFirstName);
                if (fileName.toUpperCase() !== expectedFileName.toUpperCase()) {
                    setRenameFileAs(expectedFileName);
                }
            }
        }
    }

    const resetUpload = () => {
        setPercentUploaded(0)
        setIsUploadComplete(false)
        setUploadError(null)
        setCancelUpload(false)
        setCancelSource(null)
        setHasResolvedConflicts(false)
    }

    const onUploadProgress = e => {
        if (uploadError && uploadError === ErrorTypes.CANCELLED) {
            return // bail! we've cancelled the upload, stop processing these.
        }
        if (e.type === 'progress' && e.lengthComputable) {
            let percentage = (100 * e.loaded) / e.total
            setPercentUploaded(percentage)
        }
    }

    const getUploadValidationError = (response) => {
        const errorBody = response?.data || {};
        const errorType = errorBody?.type;
        const errorCode = errorBody?.errorCode;

        const normalizedCode = `${errorCode || errorType || ''}`.toLowerCase();
        if (normalizedCode === `${ErrorTypes.FILE_TOO_BIG}`.toLowerCase()) {
            return ErrorTypes.FILE_TOO_BIG;
        }
        if (normalizedCode === `${ErrorTypes.INVALID_FILE_TYPE}`.toLowerCase()) {
            return ErrorTypes.INVALID_FILE_TYPE;
        }

        const firstError = errorBody?.errors?.['']?.[0] || '';
        const composedMessage = [
            errorCode,
            errorType,
            errorBody?.message,
            errorBody?.title,
            errorBody?.detail,
            firstError
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

        if (composedMessage.includes('length limit') || composedMessage.includes('size')) {
            return ErrorTypes.FILE_TOO_BIG;
        }

        if (composedMessage.includes('invalid file type')
            || composedMessage.includes('unsupported file type')
            || composedMessage.includes('file type not supported')) {
            return ErrorTypes.INVALID_FILE_TYPE;
        }

        return null;
    }

    useEffect(() => {
        // cancel the upload when the user clicks cancel
        if (cancelUpload) {
            if (cancelSource) {
                // upload is underway already (and has not already failed)
                cancelSource.cancel(ErrorTypes.CANCELLED);
            }
            if (uploadError) {
                // the failed upload exception has already been caught
                // so set the state values it would normally have set there.
                setUploadError(ErrorTypes.CANCELLED)
                onUploadCompleted(fileUpload.qPosition, ErrorTypes.CANCELLED)
            }
        }
    }, [cancelUpload, cancelSource])

    useEffect(() => {
        // this will handle external cancellations from the parent component
        if (cancel !== cancelUpload) {
            setCancelUpload(cancel)
        }
    }, [cancel])

    useEffect(() => {
        if (hasCheckedForDupes) {
            if (duplicateFileId) {
                setUploadError(ErrorTypes.FILE_EXISTS)
            } else {
                // note to future me/dev meddling in this code:
                // When choosing 'camera' for uploads in iOS, the filename will always
                // be 'image.jpg'. We need unique names, APPLE. Get with the program.
                checkIfAppleCameraUpload()
                
                // If this is a signature page upload with a valid filename,
                // rename it to the standard format: {firstName}_SignaturePage.pdf
                checkIfSignaturePageUpload()
                
                // okay, NOW we're ready to go
                setHasResolvedConflicts(true)
            }
        }
    }, [hasCheckedForDupes])

    useEffect(() => {
        if (isUploadComplete && onUploadCompleted) {
            // notify the queueing mechanism that this upload is finished
            // so it can start a new one, if needed.
            onUploadCompleted(fileUpload.qPosition, uploadError)
        }
    }, [isUploadComplete])

    useEffect(() => {
        async function uploadFile(file) {
            try {
                // cancellation token
                setCancelSource(api.cancelToken);

                // perform the upload
                let response = await doxUploaderCDMService
                    .uploadFile(handleUnauthorized, ticketId, file, onUploadProgress, api.cancelToken.token, renameFileAs)

                // On error, inspect the inner exception, as it
                // holds allll the secretzzz. otherwise, assume success.
                if (response && response.status !== 201) {
                    const validationError = getUploadValidationError(response)
                    if (validationError) {
                        setUploadError(validationError)
                    } else if (response.data.type && response.data.name === "CDM dependency failure") {
                        // handle expected errors
                        const backendErrorCode = response.data.errorCode || response.data.type
                        let expectedError = errorsWithMessaging.includes(backendErrorCode)
                        if (!expectedError) {
                            console.log(`unexpected error returned: $response.data.`)
                        }
                        setUploadError(expectedError ? backendErrorCode : ErrorTypes.UNKNOWN_ERROR)
                    } else {
                        setUploadError(ErrorTypes.UNKNOWN_ERROR)
                    }
                } else if (duplicateFileId && (renameFileAs === null || renameFileAs === '')) {
                    // delete file
                    await api.deleteFile(duplicateFileId);
                }
            } catch (ex) {
                if (api.isCancel(ex)) {
                    setUploadError(ErrorTypes.CANCELLED)
                } else {
                    setUploadError(ErrorTypes.UNKNOWN_ERROR)
                }
            } finally {
                // update this here regardless, as even on error, we want to mark it as complete
                setIsUploadComplete(true)
            }
        }

        if (isReadyToUpload && !isUploadComplete && hasResolvedConflicts) {
            uploadFile(fileUpload.file)
        }
    }, [isReadyToUpload, hasResolvedConflicts])

    return {
        percentUploaded,
        isUploadComplete,
        uploadError,
        isDuplicate: !!duplicateFileId,
        renameFileAs,
        hasResolvedConflicts,
        handleCancelClick,
        handleRetryClick,
        handleKeepBothClick,
        handleReplaceClick
    }
}